import { EXAM_REQUEST_TIMEOUT_MS, getExamRequestFailure, settleExamRequest } from '@/utils/network/examRequest';
import type { ExamRequestFailure } from '@/utils/network/examRequest';
import type { IGetExamInfo } from '@/api/start';

export { settleExamRequest } from '@/utils/network/examRequest';
export const EXAM_SUBMIT_TIMEOUT_MS = EXAM_REQUEST_TIMEOUT_MS;
export const SUFE_SUBMIT_RETRY_MS = 3_000;
export const SUFE_SUBMIT_MAX_RETRY_MS = 30_000;

export type MockExamCourse = 'listening' | 'reading' | 'writing';
export type ExamSubmissionState = 'editable' | 'processing' | 'submitted';
export type ExamSubmissionFailure = ExamRequestFailure & {
  stage: 'state' | 'save' | 'submit';
  confirmation?: ExamRequestFailure;
};

export function getExamSubmissionState(info: IGetExamInfo, recordId: number, course: MockExamCourse): ExamSubmissionState {
  if (info?.record_id !== recordId || !info[course]) {
    throw new Error('exam state does not match the active record');
  }
  const {status, submission_pending: pending} = info[course];
  if (pending === true) return 'processing';
  if (status === 4 || (course === 'writing' && status === 3 && pending === false)) return 'submitted';
  // Older servers cannot distinguish a temporary writing reservation from a
  // committed submission. Keep reconciling until a definitive result is available.
  if (status === 3) return 'processing';
  if (status === 1 || status === 2) return 'editable';
  throw new Error('unknown exam submission state');
}

export async function finishExamSubmission(options: {
  readState: (signal: AbortSignal) => Promise<ExamSubmissionState>;
  // Omit only when restoring a server-owned draft after a pending submission.
  save?: (signal: AbortSignal) => Promise<unknown>;
  submit: (signal: AbortSignal) => Promise<unknown>;
  timeoutMs?: number;
  signal?: AbortSignal;
  onFailure?: (failure: ExamSubmissionFailure) => void;
}): Promise<boolean> {
  const run = <T>(work: (signal: AbortSignal) => Promise<T>) => settleExamRequest(work, options.timeoutMs, options.signal);
  const report = (failure: ExamSubmissionFailure) => {
    try {
      options.onFailure?.(failure);
    } catch {
      // Diagnostic services must never prevent a later submission retry.
    }
  };
  let attemptedWrite = false;
  let stage: ExamSubmissionFailure['stage'] = 'state';
  try {
    const state = await run(options.readState);
    if (state === 'submitted') return true;
    attemptedWrite = true;
    if (state === 'editable' && options.save) {
      stage = 'save';
      const saved = await run(options.save);
      if (!saved || typeof saved !== 'object' || !('id' in saved) || typeof saved.id !== 'number' || !Number.isSafeInteger(saved.id) || saved.id <= 0) {
        report({stage, reason: 'request', code: 'INVALID_DRAFT_ID'});
        return false;
      }
    }
    // A writing reservation can outlive a crashed server process. Retrying the
    // submit lets the server reclaim it once stale; never overwrite its answers.
    stage = 'submit';
    const submitted = await run(options.submit);
    if (submitted == null || submitted === false) {
      if (await run(options.readState) === 'submitted') return true;
      report({stage, reason: 'request', code: 'UNCONFIRMED_SUBMIT'});
      return false;
    }
    return submitted != null && submitted !== false;
  } catch (error) {
    if (options.signal?.aborted) return false;
    const failure = {stage, ...getExamRequestFailure(error)};
    if (!attemptedWrite) {
      report(failure);
      return false;
    }
    // A rejected or timed-out response says nothing about whether the server
    // committed. Reconcile before allowing the caller to retry the write.
    try {
      if (await run(options.readState) === 'submitted') return true;
      report(failure);
    } catch (confirmationError) {
      if (!options.signal?.aborted) report({...failure, confirmation: getExamRequestFailure(confirmationError)});
    }
    return false;
  }
}
